import {
  getMetaSuccess,
  updateCountryCode,
  updateLocality,
  updateProviderId,
  updateRegionId,
} from "@App/appReducer";
import { getInventoryOgImage } from "@Pages/Search/data/helper";
import formatFilterState from "@Pages/Search/utils/formatFilterState";
import { AppThunk } from "@Store/index";
import generateApartmentSchemaData from "@Utils/generateApartmentSchemaData";
import generateRoomTypeSchemaData from "@Utils/generateRoomTypeSchemaData";
import generateVideoSchemaData from "@Utils/generateVideoSchemaData";
import getFAQSchema from "@Utils/getFAQSchema";
import getImageSchemaForInventory from "@Utils/getImageSchemaForInventory";
import { JSON_SCHEMA_REMOVAL_TEST_PAGES_SLUG } from "constants/configConstants";
import customWindowLocation from "@Utils/customWindowLocation";
import fetchInventory from "./fetchInventory";
// import fetchRegion from "./fetchRegion";
import fetchReviewMedia from "./fetchReviewMedia";
import getPropertyLink, {
  getFiltersFromQuery,
  getParams,
  getPlaceMetaDescription,
  getPlaceMetaKeywords,
  getPlaceMetaTitle,
  getPlaceCanonicalUrl,
  getPlaceRobotsTag,
  getPropertyPageNearbyDataParams,
  mergeAndFilterDuplicateReviews,
  checkWhitelabelInventoryAccess,
} from "./helper";
import {
  getPropertyPageError,
  getPropertyPageRequesting,
  getPropertyPageSuccess,
  updateInstallmentPlans,
  updateInventoryReviewData,
  updateNearbyData,
  updateNearbyLocations,
  updateNearbyProperties,
  updateBudgetFriendlyProperties,
  updateRoomTypes,
  updatePriceTrendsData,
} from "./propertyPageReducer";
import urljoin from "helpers/urljoin";
import config from "@Config/index";
import fetchOffers from "./fetchOffers";
import getRegionsBulk from "services/getRegionsBulk";
import fetchNearbyLocations from "./fetchNearbyLocations";
import fetchInstallmentPlans from "./fetchInstallmentPlans";
import fetchBudgetFriendly from "./fetchBudgetFriendly";
import fetchPriceTrends from "./fetchPriceTrends";

// Countries where WebPage schema rollout is enabled
const SCHEMA_ROLLOUT_COUNTRIES = ["AU"];

const getPropertyPageData =
  ({
    id,
    staticContext,
    url,
    regionId,
    token = "",
    selectedLang,
    userCurrency = "",
  }: any): AppThunk =>
  async (dispatch, getState) => {
    dispatch(getPropertyPageRequesting({ id, regionId }));
    const { userLanguage } = getState().appData;
    try {
      const filters = getFiltersFromQuery(url) || {};
      const filterParams = getParams(filters);
      const nearbyDataFilterParams = getPropertyPageNearbyDataParams(id);
      const { propertyPageData, appData, whitelabelData } = getState();
      const currentRoomTypes = propertyPageData.roomTypes?.data || [];
      const roomTypeIsLoading = propertyPageData.roomTypes?.isLoading;
      const internalUser = appData.isInternalUser;
      const [
        invRes,
        reviewData,
        offers,
        nearbyDataRes,
        nearbyLocationsRes,
        installmentPlansRes,
        budgetFriendlyRes,
        priceTrendsRes,
      ]: any = await Promise.all([
        fetchInventory({ id, regionId, token, selectedLang, userCurrency, useJavaApi: true }),
        fetchReviewMedia({ id, selectedLang }),
        fetchOffers({
          id,
          token,
          selectedLang,
          userCurrency,
          internalUser,
          isWhitelabel: whitelabelData?.isWhitelabel,
          partnerId: whitelabelData?.partner_id || whitelabelData.partner_uuid,
        }).catch(() => null),
        getRegionsBulk({
          ...nearbyDataFilterParams,
          ...(userLanguage !== "en" && { lang: userLanguage }),
          ...(userCurrency ? { user_currency: userCurrency } : {}),
        }),
        fetchNearbyLocations({ id }).catch(() => null),
        fetchInstallmentPlans({
          id,
          regionId,
          selectedLang,
        }).catch(() => null),
        fetchBudgetFriendly({ id, userCurrency }).catch(() => null),
        fetchPriceTrends({ id }).catch(() => null),
      ]);

      // @ts-ignore
      const formattedFilters = formatFilterState(filterParams);
      const inventoryData = invRes.data.data;

      const accessCheck = checkWhitelabelInventoryAccess({
        isInventoryWhitelabeled: inventoryData?.is_whitelabel_inventory || false,
        isWhitelabelApp: whitelabelData?.isWhitelabel || false,
        partnerIdFromState: whitelabelData?.partner_id || null,
        partnerUuidFromState: whitelabelData?.partner_uuid || null,
        inventoryData,
        staticContext,
      });

      if (accessCheck.shouldRedirect) {
        return accessCheck.redirectResult;
      }
      dispatch(updateProviderId({ providerId: `${inventoryData?.provider_id}` }));
      const { data: nearbyData } = nearbyDataRes;
      dispatch(
        updateNearbyData({
          cities: nearbyData?.data[0]?.result || [],
          areas: nearbyData?.data[1]?.result || [],
          universities: nearbyData?.data[2]?.result || [],
        }),
      );

      if (reviewData) {
        const modifiedReviewsData = mergeAndFilterDuplicateReviews(
          reviewData?.data?.data?.result,
          reviewData?.data?.data?.resultWithoutMedia,
        );
        dispatch(updateInventoryReviewData(modifiedReviewsData));
      }

      if (nearbyData?.data[3]?.result) {
        dispatch(updateNearbyProperties(nearbyData?.data[3]?.result));
      }

      // Dispatch nearby locations for map section SSR
      const nearbyLocationTabs = nearbyLocationsRes?.data?.data?.data;
      if (nearbyLocationTabs && nearbyLocationTabs.length > 0) {
        dispatch(updateNearbyLocations(nearbyLocationTabs));
      }

      // Dispatch installment plans SSR
      const rawInstallmentPlans = installmentPlansRes?.data?.data?.result;
      if (rawInstallmentPlans) {
        dispatch(updateInstallmentPlans({ installment_plans: rawInstallmentPlans }));
      }

      // Dispatch budget-friendly properties SSR
      const budgetFriendlyData = budgetFriendlyRes?.data?.data || [];
      if (budgetFriendlyData.length > 0) {
        dispatch(updateBudgetFriendlyProperties(budgetFriendlyData));
      }

      // Dispatch price trends data SSR
      const priceTrendsData = priceTrendsRes?.data?.data || null;
      if (priceTrendsData) {
        dispatch(updatePriceTrendsData(priceTrendsData));
      }

      dispatch(
        updateLocality({
          locality:
            invRes?.data?.data?.region_meta?.location?.locality?.long_name ||
            inventoryData?.location?.locality?.long_name,
        }),
      );
      dispatch(
        updateRegionId({
          regionId: inventoryData?.region?.id,
        }),
      );
      const activeRoomTypes = invRes?.data?.data?.children_short;

      const payload = {
        data: activeRoomTypes,
        isLoading: true,
        count: activeRoomTypes?.length || 0,
      };

      if (!__CLIENT__ || (currentRoomTypes.length === 0 && roomTypeIsLoading === false)) {
        dispatch(updateRoomTypes(payload));
      }
      const regionNameObj = invRes.data.data.region_meta;
      dispatch(updateCountryCode({ countryCode: inventoryData?.location?.country?.short_name }));
      const regionName = regionNameObj ? regionNameObj?.name : " City Center";
      const regionType = regionNameObj ? regionNameObj?.region_type : "";
      inventoryData["redirect_region_name"] = regionName;
      inventoryData["redirect_region_type"] = regionType;
      inventoryData["active_offers"] = offers?.data?.data || [];

      const inventoryUrl = urljoin(config.WEB_SERVICE_URL, getPropertyLink(inventoryData, {}));
      inventoryData["inventoryUrl"] = inventoryUrl;

      if (inventoryData.parent_id) {
        console.log(`'Inventory Child: ', ${id}`);
        if (staticContext) {
          staticContext.statusCode = 404;
        }
        return dispatch(getPropertyPageError("404"));
      }
      // Deleted inventories. Pass 404 as the status message.
      if (inventoryData.status == "deleted") {
        if (inventoryData.region?.canonical_name) {
          staticContext.url = `/places/search/${inventoryData.region.canonical_name}`;
        } else {
          staticContext.url = `/`;
        }
        if (__CLIENT__) customWindowLocation({ url: staticContext.url });
        return null;
      }
      // Check if the canonical url is valid
      if (id != inventoryData.canonical_name) {
        staticContext.url = getPropertyLink(inventoryData, {});
        if (__CLIENT__) customWindowLocation({ url: staticContext.url });
        return null;
      }
      const meta: any = {};
      const videoSchemaData = generateVideoSchemaData(inventoryData);
      const imageSchemaData = getImageSchemaForInventory(inventoryData);
      const faqSchemaData = JSON_SCHEMA_REMOVAL_TEST_PAGES_SLUG.includes(id)
        ? []
        : getFAQSchema(inventoryData.faqs);
      const apartmentSchemaData = generateApartmentSchemaData(inventoryData, inventoryUrl);
      const roomtypeSchemaData = generateRoomTypeSchemaData(inventoryData, inventoryUrl);
      const schemaData = [
        videoSchemaData,
        imageSchemaData,
        faqSchemaData,
        apartmentSchemaData,
        roomtypeSchemaData,
      ].flat();

      // Check if property is in schema rollout countries
      const isSchemaEnabled = SCHEMA_ROLLOUT_COUNTRIES.includes(
        inventoryData?.location?.country?.short_name,
      );

      // WebPage schema for property pages (gated by schema rollout countries)
      if (isSchemaEnabled) {
        const propertyPageUrl = inventoryUrl;
        const propertyWebPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${propertyPageUrl}#webpage`,
          url: propertyPageUrl,
          name: getPlaceMetaTitle(inventoryData, userLanguage),
        };
        if (inventoryData?.region?.canonical_name) {
          const cityUrl = urljoin(
            config.WEB_SERVICE_URL,
            `/places/search/${inventoryData.region.canonical_name}`,
          );
          propertyWebPageSchema.isPartOf = {
            "@type": "WebPage",
            "@id": `${cityUrl}#webpage`,
            url: cityUrl,
          };
        }
        schemaData.push(propertyWebPageSchema);
      }
      meta.url = url || "";
      if (inventoryData?.name) {
        meta.title_prefix = getPlaceMetaTitle(inventoryData, userLanguage);
        meta.description = getPlaceMetaDescription(inventoryData, userLanguage);
        meta.canonical_url_suffix = getPlaceCanonicalUrl(inventoryData, userLanguage);
        meta.keywords = getPlaceMetaKeywords(inventoryData);

        // Check for custom robots tag from inventory meta
        const customRobotsTag = getPlaceRobotsTag(inventoryData);
        if (customRobotsTag) {
          meta.robots = customRobotsTag;
        }

        meta.ogImage = getInventoryOgImage(inventoryData, "medium");
        meta.schemaData = schemaData;
      }
      meta.showZhDescription = selectedLang === "zh";
      dispatch(getMetaSuccess(meta));
      dispatch(
        getPropertyPageSuccess({
          data: invRes?.data?.data,
          reviewData: reviewData?.data?.data,
          filters: formattedFilters,
        }),
      );

      return null;
    } catch (err) {
      console.error(err);
      return dispatch(getPropertyPageError(JSON.stringify(err)));
    }
  };

export default getPropertyPageData;
